import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import HeartParticles from './HeartParticles';
import ShareButton from './ShareButton';
import { LOVE_MILESTONES, LOVE_QUOTES } from '../data/loveData';
import { generateSEOFriendlyURL } from '../utils/shareUtils';

const LoveResults = ({ 
  language, 
  calculatedDays, 
  detailedTime, 
  relationshipLabel, 
  resultsVisible, 
  anniversaryDate, 
  anniversaryType,
  calculationCount // 新增：接收計算次數
}) => {
  const [showParticles, setShowParticles] = useState(false);
  const [currentQuote, setCurrentQuote] = useState('');

  // 每次計算都觸發心心特效
  useEffect(() => {
    if (resultsVisible && calculatedDays > 0 && calculationCount > 0) {
      setShowParticles(true);
      const timer = setTimeout(() => setShowParticles(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [resultsVisible, calculatedDays, calculationCount]); // 添加 calculationCount 依賴

  // 每次計算都生成新的金句
  useEffect(() => {
    if (resultsVisible && calculatedDays > 0 && calculationCount > 0) {
      setCurrentQuote(getRandomQuote());
    }
  }, [resultsVisible, calculatedDays, language, calculationCount]); // 添加 calculationCount 依賴

  const getCurrentMilestone = () => {
    let currentIndex = -1;
    for (let i = 0; i < LOVE_MILESTONES.length; i++) {
      if (calculatedDays >= LOVE_MILESTONES[i]) {
        currentIndex = i;
      } else {
        break;
      }
    }
    return currentIndex;
  };

  const getMilestoneProgress = () => {
    const currentIndex = getCurrentMilestone();
    const current = currentIndex >= 0 ? LOVE_MILESTONES[currentIndex] : 0;
    const next = currentIndex < LOVE_MILESTONES.length - 1 ? LOVE_MILESTONES[currentIndex + 1] : current * 2;
    const progress = Math.min(100, Math.floor(((calculatedDays - current) / (next - current)) * 100));

    return {
      current,
      next,
      progress,
      daysToNext: next - calculatedDays
    };
  };

  const getRandomQuote = () => {
    const quotes = LOVE_QUOTES[language] || [];
    return quotes[Math.floor(Math.random() * quotes.length)];
  };

  if (!resultsVisible) {
    return (
      <div className="bg-gradient-to-br from-white to-love-50 dark:from-gray-800 dark:to-gray-850 p-5 rounded-xl border border-love-100 dark:border-love-900 shadow-sm">
        <h2 className="text-lg font-medium mb-4 flex items-center">
          <span className="emoji animate-heartbeat mr-2">❤️</span>
          {language === 'zh' ? '我們的愛情時光' : 'Our Love Journey'}
        </h2>
        <div className="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
          <div className="mb-4 opacity-80">
            <span className="big-heart-emoji">❤️</span>
          </div>
          <p>{language === 'zh' ? '請選擇紀念日期，記錄你們的愛情時光' : 'Select an anniversary date to track your love journey'}</p>
        </div>
      </div>
    );
  }

  const { current, next, progress, daysToNext } = getMilestoneProgress();

  // Generate SEO-friendly URL for sharing
  const shareUrl = anniversaryDate ? generateSEOFriendlyURL({ anniversaryDate, anniversaryType, language }) : window.location.href;

  return (
    <motion.div
      className="bg-gradient-to-br from-white to-love-50 dark:from-gray-800 dark:to-gray-850 p-5 rounded-xl border border-love-100 dark:border-love-900 shadow-sm"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      // 添加 key 以確保每次計算都重新渲染
      key={`love-results-${calculationCount}`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium flex items-center">
          <span className="emoji animate-heartbeat mr-2">❤️</span>
          {language === 'zh' ? `我們${relationshipLabel}的日子` : `Our Days ${relationshipLabel}`}
        </h2>
        
        {/* Share Button Integration - 傳遞統一的金句 */}
        <ShareButton
          language={language}
          calculatedDays={calculatedDays}
          detailedTime={detailedTime}
          relationshipLabel={relationshipLabel}
          anniversaryDate={anniversaryDate}
          anniversaryType={anniversaryType}
          shareUrl={shareUrl}
          quote={currentQuote} // 傳遞統一的金句
        />
      </div>

      {/* Days Counter */}
      <motion.div
        className="text-center p-4 mb-4 bg-white dark:bg-gray-750 rounded-lg shadow-sm border border-love-100 dark:border-love-900 relative overflow-hidden"
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200 }}
        // 每次計算都觸發動畫
        key={`days-counter-${calculationCount}`}
      >
        <motion.div
          className="text-5xl font-bold text-love-500 mb-2 animate-pulse-slow"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          aria-live="polite"
          aria-atomic="true"
        >
          {calculatedDays}
        </motion.div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          <span className="font-medium">{relationshipLabel}</span>
          {language === 'zh' ? '的日子' : ' days together'}
        </div>
        {showParticles && <HeartParticles />}
      </motion.div>

      {/* Detailed Time Breakdown */}
      {detailedTime && (
        <motion.div
          className="grid grid-cols-3 gap-3 mb-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          key={`detailed-time-${calculationCount}`}
        >
          <div className="bg-white dark:bg-gray-750 p-3 rounded-lg text-center border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-xl font-semibold text-love-700 dark:text-love-300">{detailedTime.years}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'zh' ? '年' : 'Years'}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-750 p-3 rounded-lg text-center border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-xl font-semibold text-love-700 dark:text-love-300">{detailedTime.months}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'zh' ? '月' : 'Months'}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-750 p-3 rounded-lg text-center border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow">
            <div className="text-xl font-semibold text-love-700 dark:text-love-300">{detailedTime.days}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {language === 'zh' ? '日' : 'Days'}
            </div>
          </div>
        </motion.div>
      )}

      {/* Love Quote - 顯示統一的金句，每次計算都會更新 */}
      {currentQuote && (
        <motion.div
          className="mt-4 mb-5 text-center p-4 bg-white dark:bg-gray-750 rounded-lg shadow-sm border border-love-100 dark:border-love-900"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          key={`quote-${calculationCount}`} // 每次計算都重新渲染金句
        >
          <div className="text-love-600 dark:text-love-300 italic text-sm">{currentQuote}</div>
        </motion.div>
      )}

      {/* Milestone Celebration */}
      <motion.div
        className="mt-4 flex justify-center"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8 }}
        key={`milestone-${calculationCount}`}
      >
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-love-100 dark:bg-love-900 text-love-800 dark:text-love-200 shadow-sm">
          <span className="emoji mr-2">🎉</span>
          <span className="text-sm font-medium">
            {current > 0
              ? (language === 'zh' ? `恭喜達到 ${current} 天里程碑！` : `Congratulations on reaching ${current} days!`)
              : (language === 'zh' ? `距離第一個里程碑還有 ${daysToNext} 天` : `${daysToNext} days until your first milestone`)}
          </span>
        </div>
      </motion.div>

      {/* Next Milestone Progress */}
      <motion.div
        className="mt-6 bg-white dark:bg-gray-750 p-4 rounded-lg shadow-sm border border-love-100 dark:border-love-900"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
        key={`progress-${calculationCount}`}
      >
        <div className="flex justify-between text-xs mb-2">
          <div>
            <span>{current}</span>
            <span className="ml-1">{language === 'zh' ? '天' : 'days'}</span>
          </div>
          <div>
            <span>{next}</span>
            <span className="ml-1">{language === 'zh' ? '天' : 'days'}</span>
          </div>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-2 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100">
          <motion.div
            className="bg-love-500 h-2.5 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 1, delay: 1.2 }}
          />
        </div>
        <div className="text-xs text-center text-gray-500 dark:text-gray-400">
          {language === 'zh' ? '距離下一個里程碑還有 ' : 'Days to next milestone: '}
          <span className="font-medium">{daysToNext}</span>
          {language === 'zh' ? ' 天' : ' days'}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LoveResults;